import type {
  AdvisorDraft,
  AdvisorDraftLeg,
  AdvisorItemKind,
  AdvisorPoint,
  AdvisorProduct,
} from './types';
import { productUsualHours } from '@travel-planner/shared/utils/product-usual-hours';
import { findChapter } from './add-item';
import { isUuid } from './to-query-input';

export { productDurationMinutes } from '@travel-planner/shared/utils/product-card';

// Mini Planner v3 result cards: the metric row (price · duration · effort ·
// to hotel), in-itinerary state, day targets and sorting.

export const PLANNER_V3_SORTS = [
  { value: 'relevance', label: 'Best match' },
  { value: 'price', label: 'Price ↑' },
  { value: 'priceDesc', label: 'Price ↓' },
  { value: 'booked', label: 'Most booked' },
  { value: 'distance', label: 'Closest to hotel' },
  { value: 'duration', label: 'Shortest' },
] as const;

export type PlannerV3Sort = (typeof PLANNER_V3_SORTS)[number]['value'];

// XT-840: only the orders that mean something for the type on screen.
const SORTS_BY_KIND: Record<AdvisorItemKind, readonly PlannerV3Sort[]> = {
  hotel: ['relevance', 'price', 'priceDesc', 'booked'],
  experience: ['relevance', 'price', 'priceDesc', 'distance', 'duration'],
  restaurant: ['relevance', 'price', 'priceDesc', 'distance'],
};

export const plannerSortsFor = (kind: AdvisorItemKind) =>
  PLANNER_V3_SORTS.filter((option) =>
    SORTS_BY_KIND[kind].includes(option.value)
  );

/** A chapter's hotel, by the region its products are measured in. */
export type PlannerNearHotel = { regionId: string; lat: number; lng: number };

/** Sorts the server applies to the whole catalogue (TAP01-059, TAP01-073). */
export type PlannerServerSort = {
  sortBy:
    | 'plannerPrice'
    | 'roomNightsSold'
    | 'plannerDistance'
    | 'plannerDuration';
  sortOrder: 'asc' | 'desc';
  nearHotels?: PlannerNearHotel[];
};

const SERVER_SORTS: Record<
  Exclude<PlannerV3Sort, 'relevance' | 'distance'>,
  PlannerServerSort
> = {
  price: { sortBy: 'plannerPrice', sortOrder: 'asc' },
  priceDesc: { sortBy: 'plannerPrice', sortOrder: 'desc' },
  booked: { sortBy: 'roomNightsSold', sortOrder: 'desc' },
  duration: { sortBy: 'plannerDuration', sortOrder: 'asc' },
};

/**
 * The catalogue order to ask for: none for Best match, and none for Closest
 * to hotel while no chapter has a hotel to measure from.
 */
export const serverSortOf = (
  sort: PlannerV3Sort,
  nearHotels: readonly PlannerNearHotel[] = []
): PlannerServerSort | null => {
  if (sort === 'relevance') return null;
  if (sort !== 'distance') return SERVER_SORTS[sort];
  return nearHotels.length > 0
    ? {
        sortBy: 'plannerDistance',
        sortOrder: 'asc',
        nearHotels: [...nearHotels],
      }
    : null;
};

/** The sort in effect: one the type does not offer is Best match. */
export const effectiveSort = (
  sort: PlannerV3Sort,
  kind: AdvisorItemKind
): PlannerV3Sort => (SORTS_BY_KIND[kind].includes(sort) ? sort : 'relevance');

/**
 * `{lat,lng}` or the list row's PostGIS tuple, which is `[lng,lat]`; the
 * (0,0) "no point" sentinel reads as none.
 */
export const productPoint = (value: unknown): AdvisorPoint | null => {
  const [lat, lng] = Array.isArray(value)
    ? [value[1], value[0]]
    : value && typeof value === 'object'
      ? [(value as { lat?: unknown }).lat, (value as { lng?: unknown }).lng]
      : [];
  if (typeof lat !== 'number' || typeof lng !== 'number') return null;
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  if (lat === 0 && lng === 0) return null;
  return { lat, lng };
};

/** The product's usual start or end (`productUsualHours`), minutes past midnight. */
export const productClockMinutes = (
  legs: unknown,
  which: 'startTime' | 'endTime'
): number | null => {
  const hours = productUsualHours(legs);
  if (!hours) return null;
  return which === 'startTime' ? hours.startMinutes : hours.endMinutes;
};

export const formatDuration = (
  kind: AdvisorItemKind,
  minutes: number | null | undefined
): string => {
  if (kind === 'hotel') return 'Night';
  if (!minutes) return '—';
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (hours === 0) return `${rest} min`;
  return rest === 0
    ? `${hours}h`
    : `${hours}h ${String(rest).padStart(2, '0')}`;
};

export type PriceLabel = {
  text: string;
  unit: string | null;
  onRequest: boolean;
};

// `pricing_type.type` → what the card's price is per.
const PRICE_UNITS: Record<string, string> = {
  pax: '/ pp',
  night: '/ night',
  day: '/ day',
  hour: '/ hour',
  vehicle: '/ vehicle',
  piece: '/ piece',
  unit: '/ unit',
};

/**
 * Null or 0 is "On request" — a price is never shown as 0. The unit follows
 * the product's pricing type and is left off when the product has none.
 */
export const priceLabel = (
  clientPrice: number | null,
  currency: string | null,
  pricingType: string | null | undefined
): PriceLabel => {
  if (!clientPrice || clientPrice <= 0) {
    return { text: 'On request', unit: null, onRequest: true };
  }
  const text = new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: currency || 'EUR',
    maximumFractionDigits: 0,
  }).format(clientPrice);
  const unit = PRICE_UNITS[(pricingType ?? '').trim().toLowerCase()] ?? null;
  return { text, unit, onRequest: false };
};

const EARTH_KM = 6371;
const rad = (deg: number) => (deg * Math.PI) / 180;

export const haversineKm = (a: AdvisorPoint, b: AdvisorPoint): number => {
  const dLat = rad(b.lat - a.lat);
  const dLng = rad(b.lng - a.lng);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_KM * Math.asin(Math.min(1, Math.sqrt(h)));
};

export const formatKm = (km: number): string =>
  km < 1 ? '< 1 km' : km < 10 ? `${km.toFixed(1)} km` : `${Math.round(km)} km`;

/**
 * Where the chapter's stay is: its base hotel, else the first hotel on its
 * days with a map point. A chapter with neither has no distances yet.
 */
export const chapterHotelPoint = (
  leg: AdvisorDraftLeg
): AdvisorPoint | null => {
  if (leg.baseHotel?.location) return leg.baseHotel.location;
  for (const day of leg.days) {
    for (const item of day.items) {
      if (item.kind === 'hotel' && item.location) return item.location;
    }
  }
  return null;
};

/** Each chapter's hotel, for Closest to hotel; a region's first chapter counts. */
export const nearHotelsOf = (draft: AdvisorDraft): PlannerNearHotel[] => {
  const seen = new Set<string>();
  return draft.legs.flatMap((leg) => {
    if (!isUuid(leg.regionId) || seen.has(leg.regionId)) return [];
    seen.add(leg.regionId);
    const point = chapterHotelPoint(leg);
    return point ? [{ regionId: leg.regionId, ...point }] : [];
  });
};

export type ToHotel =
  | { kind: 'km'; km: number }
  | { kind: 'no-hotel' }
  | { kind: 'unknown' };

export const toHotel = (
  draft: AdvisorDraft,
  product: Pick<AdvisorProduct, 'regionId' | 'location'>
): ToHotel => {
  if (!product.regionId) return { kind: 'unknown' };
  const leg = findChapter(draft.legs, product);
  const hotel = leg ? chapterHotelPoint(leg) : null;
  if (!hotel) return { kind: 'no-hotel' };
  if (!product.location) return { kind: 'unknown' };
  return { kind: 'km', km: haversineKm(product.location, hotel) };
};

/** "Cascais · Lisbon"; the region is left out when it only repeats the place. */
export const placeLine = (
  place: string | null,
  regionTitle: string,
  displayTitle: (title: string) => string = (title) => title
): string => {
  const region = displayTitle(regionTitle);
  const repeats =
    !region ||
    regionTitle === 'Unassigned' ||
    (place ?? '').trim().toLowerCase() === region.trim().toLowerCase() ||
    (place ?? '').trim().toLowerCase() === regionTitle.trim().toLowerCase();
  return [place, repeats ? null : region].filter(Boolean).join(' · ');
};

export const itineraryProductIds = (draft: AdvisorDraft): Set<string> =>
  new Set(
    draft.legs.flatMap((leg) => [
      ...(leg.baseHotel ? [leg.baseHotel.productId] : []),
      ...leg.days.flatMap((day) => day.items.map((item) => item.productId)),
    ])
  );

export const baseHotelProductIds = (draft: AdvisorDraft): Set<string> =>
  new Set(
    draft.legs.flatMap((leg) =>
      leg.baseHotel ? [leg.baseHotel.productId] : []
    )
  );

export type DayTarget = {
  dayId: string;
  label: string;
  /** The product is already on this day. */
  taken: boolean;
};

/** "Lisbon · Day 2" — days numbered across the whole trip. */
export const dayTargets = (
  draft: AdvisorDraft,
  productId: string,
  displayTitle: (title: string) => string = (title) => title,
  regionId: string | null = null
): DayTarget[] => {
  let n = 0;
  // Days keep their trip-wide numbers; only the product's region's are offered.
  return draft.legs.flatMap((leg) =>
    leg.days.flatMap((day) => {
      n += 1;
      if (regionId && leg.regionId !== regionId) return [];
      return [
        {
          dayId: day.id,
          label: `${displayTitle(leg.regionTitle)} · Day ${n}`,
          taken: day.items.some((item) => item.productId === productId),
        },
      ];
    })
  );
};

/**
 * Distance and duration also order the page as its cards read (the catalogue
 * already sorted it, `serverSortOf`); the other orders are the catalogue's.
 * Unknowns (no hotel, no times) go last and ties keep catalogue order.
 */
export const sortPage = <T>(
  rows: readonly T[],
  sort: PlannerV3Sort,
  key: (row: T) => { km: number | null; minutes: number | null }
): T[] => {
  if (sort !== 'distance' && sort !== 'duration') return [...rows];
  const value = (row: T) => {
    const { km, minutes } = key(row);
    return (sort === 'distance' ? km : minutes) ?? Number.POSITIVE_INFINITY;
  };
  return rows
    .map((row, index) => ({ row, index }))
    .sort((a, b) => value(a.row) - value(b.row) || a.index - b.index)
    .map((entry) => entry.row);
};
